import { Component, OnDestroy } from '@angular/core'
import { Store } from '@ngrx/store'
import {
    selectCaseHasSavedHierarchicalSelections,
    selectHierarchicalSelectionRootName,
    selectSavedHierarchicalSelectionRootNames,
    selectSelectionState,
} from '../../../state/selection/selection.selectors'
import { selectionActions } from '../../../state/selection/selection.actions'
import { Subject, takeUntil, take, Observable, combineLatest } from 'rxjs'
import {
    selectCaseSegObjectPropertyMap,
    selectIsSharedCase,
    selectLoadedSegSources,
} from '../../../state/case/case.selectors'
import { GenericConfirmationDialogModel } from '../../../shared/components/generic-confirmation-dialog/generic-confirmation-dialog.model'
import { Dialog } from '@angular/cdk/dialog'
import { GenericConfirmationDialogComponent } from '../../../shared/components/generic-confirmation-dialog/generic-confirmation-dialog.component'
import { SaveSelectionAsDialogComponent } from '../../save-selection-as-dialog/save-selection-as-dialog.component'
import { DownloadService } from '../../../shared/services/download.service'
import { HttpErrorResponse } from '@angular/common/http'
import { ToasterService } from '../../../shared/services/toaster.service'
import { SelectionType } from 'src/app/state/selection/selection.state'
import { topLevelSelectionName } from 'src/app/state/selection/selection.reducer'

@Component({
    selector: 'app-saved-hierarchical-selection-roots',
    templateUrl: './saved-hierarchical-selection-roots.component.html',
    styleUrls: ['./saved-hierarchical-selection-roots.component.scss'],
})
export class SavedHierarchicalSelectionRootsComponent implements OnDestroy {
    readonly hasSavedHierarchicalSelections$ = this.store.select(
        selectCaseHasSavedHierarchicalSelections
    )
    readonly currentHierarchicalSelectionRootName$ = this.store.select(
        selectHierarchicalSelectionRootName
    )

    savedHierarchicalSelectionNames$: Observable<string[]>
    readonly isSharedCase$ = this.store.select(selectIsSharedCase)
    readonly selectionState$ = this.store.select(selectSelectionState)
    isUnsupervisedSelection = false

    private readonly destroy$ = new Subject<void>()

    constructor(
        private readonly store: Store,
        private readonly dialog: Dialog,
        private readonly toaster: ToasterService,
        private readonly downloader: DownloadService
    ) {
        this.selectionState$.pipe().subscribe(selectionState => {
            if (selectionState.type === SelectionType.Hierarchical) {
                this.isUnsupervisedSelection = false
            } else {
                this.isUnsupervisedSelection = true
            }
            this.savedHierarchicalSelectionNames$ = this.store.select(
                selectSavedHierarchicalSelectionRootNames
            )
        })
    }

    readonly currentSegObjectPropertyMap$ = this.store.select(selectCaseSegObjectPropertyMap)

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    getHasSavedSelections(): boolean {
        let rval = false

        this.hasSavedHierarchicalSelections$
            .pipe(take(1))
            .subscribe(hasSavedHierarchicalSelections => {
                rval = hasSavedHierarchicalSelections
            })

        return rval
    }

    createNewHierarchicalSelectionRoot(): void {
        const dialogRef = this.dialog.open<string>(SaveSelectionAsDialogComponent, {
            autoFocus: 'first-tabbable',
        })

        combineLatest([
            this.currentSegObjectPropertyMap$,
            this.store.select(selectLoadedSegSources),
        ])
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(([_segObjectPropertyMap, loadedSegSources]) => {
                dialogRef.closed.pipe(takeUntil(this.destroy$)).subscribe(name => {
                    if (!name) return
                    if (this.isUnsupervisedSelection) {
                        this.store.dispatch(
                            selectionActions.saveNewAutoCellTypeDetectionRoot({
                                name,
                                segSources: loadedSegSources,
                            })
                        )
                        this.store.dispatch(
                            selectionActions.setVisibleSelectionsOnScatterPlot({
                                selectionNames: [topLevelSelectionName],
                            })
                        )
                    } else {
                        this.store.dispatch(
                            selectionActions.saveNewHierarchicalSelectionRoot({
                                name,
                                segSources: loadedSegSources,
                            })
                        )
                        this.store.dispatch(
                            selectionActions.setHierarchicalSelectionNode({
                                name: topLevelSelectionName,
                            })
                        )
                        this.store.dispatch(
                            selectionActions.setVisibleSelectionsOnScatterPlot({
                                selectionNames: [topLevelSelectionName],
                            })
                        )
                    }
                })
            })
    }

    setSelection(name: string): void {
        this.store.dispatch(selectionActions.setHierarchicalSelectionRoot({ name }))
        if (!this.isUnsupervisedSelection) {
            this.store.dispatch(
                selectionActions.setHierarchicalSelectionNode({
                    name: topLevelSelectionName,
                })
            )
        }
    }

    downloadHierarchicalSelections(): void {
        this.currentHierarchicalSelectionRootName$.pipe(take(1)).subscribe(rootName => {
            if (!rootName) return
            if (this.isUnsupervisedSelection) {
                this.downloader.downloadUnsupervisedSelections(rootName).subscribe({
                    error: (err: HttpErrorResponse) => {
                        this.toaster.httpError(err, 'Failed to download selections.')
                    },
                })
            } else {
                this.downloader.downloadHierarchicalSelections(rootName).subscribe({
                    error: (err: HttpErrorResponse) => {
                        this.toaster.httpError(err, 'Failed to download selections.')
                    },
                })
            }
        })
    }

    deleteCurrentHierarchicalSelectionRoot(selectionName: string | undefined): void {
        const data: GenericConfirmationDialogModel = {
            title: `Are you sure you want to delete this ${
                this.isUnsupervisedSelection ? 'unsupervised' : 'hierarchical'
            } selection?`,
            item: selectionName,
            confirmButtonLabel: 'Delete',
            cancelButtonLabel: 'Cancel',
        }
        const dialogRef = this.dialog.open<string>(GenericConfirmationDialogComponent, {
            data,
            autoFocus: 'dialog',
        })
        dialogRef.closed.pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
            if (!confirmed) return
            this.store.dispatch(
                this.isUnsupervisedSelection
                    ? selectionActions.deleteAutoCellTypeDetectionRoot({ name: selectionName! })
                    : selectionActions.deleteHierarchicalSelectionRoot({ name: selectionName! })
            )
        })
    }
}
